import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, Inject } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Observable, combineLatest, map, shareReplay, startWith } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  DashboardParameterCatalogOption,
  DashboardRoleEditDialogService,
  DashboardRoleEditDialogVm
} from './dashboard-role-edit-dialog.service';

export interface DashboardRoleParameter {
  key: string;
  value: any;
}

export interface DashboardRoleRow {
  uid: string;
  role: string;
  email: string;
  name: string;
  parameters: DashboardRoleParameter[];
}

interface DashboardParameterObjectItem {
  label: string;
  type: string;
}

interface DashboardRoleEditDialogData {
  role: DashboardRoleRow;
  parameters_objects: Observable<Record<string, DashboardParameterObjectItem>>;
}

@Component({
  selector: 'app-dashboard-role-edit-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule
  ],
  templateUrl: './dashboard-role-edit-dialog.component.html',
  styleUrl: './dashboard-role-edit-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DashboardRoleEditDialogComponent {
  readonly form_group: FormGroup;
  readonly parameters_array: FormArray<FormGroup>;
  readonly catalog_options$: Observable<DashboardParameterCatalogOption[]>;
  readonly vm$: Observable<DashboardRoleEditDialogVm>;
  private latest_catalog_options: DashboardParameterCatalogOption[] = [];

  constructor(
    private readonly fb: FormBuilder,
    private readonly dialog_ref: MatDialogRef<DashboardRoleEditDialogComponent>,
    private readonly dashboard_role_edit_dialog_service: DashboardRoleEditDialogService,
    private readonly destroy_ref: DestroyRef,
    @Inject(MAT_DIALOG_DATA) public readonly data: DashboardRoleEditDialogData
  ) {
    this.form_group = this.fb.group({
      email: [this.data.role.email, [Validators.required, Validators.email]],
      parameters: this.fb.array(
        (this.data.role.parameters ?? []).map((parameter) => this.create_parameter_group(parameter))
      )
    });
    this.parameters_array = this.form_group.get('parameters') as FormArray<FormGroup>;

    this.catalog_options$ = this.data.parameters_objects.pipe(
      startWith({}),
      map((parameters_objects) =>
        this.dashboard_role_edit_dialog_service.build_catalog_options(parameters_objects ?? {}, this.data.role.parameters ?? [])
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.catalog_options$.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe((catalog_options) => {
      this.latest_catalog_options = catalog_options;
    });

    this.vm$ = combineLatest([
      this.form_group.valueChanges.pipe(startWith(this.form_group.getRawValue())),
      this.catalog_options$
    ]).pipe(
      map(([_, catalog_options]) => this.dashboard_role_edit_dialog_service.build_view_model(this.parameters_array, catalog_options)),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  add_parameter(): void {
    // Agrega una fila nueva solo si existe catalogo de parameters_objects.
    if (!this.latest_catalog_options.length) {
      return;
    }

    this.parameters_array.push(this.create_parameter_group());
  }

  remove_parameter(index: number): void {
    // Elimina una fila del formulario reactivo.
    this.parameters_array.removeAt(index);
  }

  save(): void {
    // Normaliza el formulario y devuelve el acceso actualizado.
    if (this.form_group.invalid) {
      this.form_group.markAllAsTouched();
      return;
    }

    const email = String(this.form_group.get('email')?.value ?? '').trim().toLowerCase();
    const parameters = this.parameters_array.getRawValue()
      .map((parameter) => ({
        key: String(parameter['key'] ?? '').trim(),
        value: String(parameter['value'] ?? '').trim()
      }))
      .filter((parameter) => parameter.key.length > 0);

    this.dialog_ref.close({
      ...this.data.role,
      email,
      parameters
    } satisfies DashboardRoleRow);
  }

  private create_parameter_group(parameter?: Partial<DashboardRoleParameter>): FormGroup {
    // Crea la fila base de clave y valor.
    const default_key = parameter?.key ?? this.latest_catalog_options[0]?.key ?? '';
    return this.fb.group({
      key: [parameter?.key ?? default_key, Validators.required],
      value: [parameter?.value ?? '']
    });
  } 
}
